import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { from, Observable, of, Subject } from 'rxjs';
import { filter, map, mergeMap, timeout } from 'rxjs/operators';

import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';

import { AppNotificationsService } from '@libs/portal-common';
import { CallsMapType, IConversation, IIntegrationAppService, IOutgoingConversation } from '../../../abstractions';
import { AppConfigurationService, APP_CONFIGURATION } from '../../../services/app-configuration.service';
import { CC4AllConversation } from '../cc4all-conversation';
import { CC4AllSocket } from './cc4all-socket';
import { CallSession, PresenceAndNote } from '../model';
import { CC4AllWarmTransferConversation } from '../cc4all-warm-transfer-conversation';
import { CC4AllOutboundConversation } from '../cc4all-outbound-conversation';

export const BUSY_PRESENSE = 'Busy';
export const BUSY_NOTE = 'OnBreak';

@Injectable()
export class CC4AllAppService implements IIntegrationAppService {
  private webSocket: CC4AllSocket;
  private _outgoingCall = new Subject<IOutgoingConversation>();

  public get name(): string {
    return 'CC4All';
  }

  public get calls$(): Observable<CallsMapType> {
    return of({});
  }

  get socket(): CC4AllSocket {
    return this.webSocket;
  }

  public get presenceAndNote$(): Observable<PresenceAndNote> {
    return this.webSocket.presenceAndNote$;
  }

  public get callSessions$(): Observable<CallSession> {
    return this.webSocket.callSessions$;
  }

  public get isAuthenticated$(): Observable<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(true);
    }
    return this.webSocket.authenticated$;
  }

  public get initialized$(): Observable<boolean> {
    if (!!this.webSocket) {
      return this.webSocket.initialized$;
    }
    return of(false);
  }

  get outgoingCall$(): Observable<IOutgoingConversation> {
    return this._outgoingCall.asObservable();
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(APP_CONFIGURATION) private configuration: AppConfigurationService,
    private notifications: AppNotificationsService,
    private msalService: MsalService,
  ) {
    this.initialize();
  }

  private initialize() {
    if (isPlatformBrowser(this.platformId) && this.configuration.data.integration.type === 'cc4all' && !this.webSocket) {
      this.webSocket = new CC4AllSocket(
        this.configuration.data.integration.cc4all_settings.socketsServerUrl,
        this.configuration.data.integration.cc4all_settings.cc4AllTenant,
        this.platformId,
        this.configuration.data.integration.cc4all_settings.debug,
      );
    }
  }

  public signInAAD(): Observable<boolean> {
    return from((async () => {
      this.initialize();

      const clientId = this.configuration.data.integration.cc4all_settings.aadClientId;
      const request = {
        scopes: ['openid', 'email', 'profile', `${clientId}/.default`],
        account: this.msalService.instance.getActiveAccount(),
      };

      let authResult: AuthenticationResult | null = null;

      try {
        if (request.account) {
          authResult = await this.msalService.instance.acquireTokenSilent(request);
        } else {
          authResult = await this.msalService.instance.acquireTokenPopup(request);
        }
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          try {
            authResult = await this.msalService.instance.acquireTokenPopup(request);
          } catch (popupError) {
            console.error(popupError);
            this.notifications.error(popupError.message || 'Login failed');
            return false;
          }
        } else {
          console.error(error);
          this.notifications.error(error.message || 'An unexpected error occurred');
          return false;
        }
      }

      if (authResult) {
        this.msalService.instance.setActiveAccount(authResult.account);
        const webSocketResponse = await this.webSocket.aadSignIn(authResult.accessToken).toPromise();
        return !!webSocketResponse.JWT;
      }

      return false;
    })());
  }

  public signIn(username: string, password: string): Observable<boolean> {
    this.initialize();
    return this.webSocket.signIn(username, password).pipe(map((res) => !!res.JWT));
  }

  public signOut(): Observable<any> {
    return from((async () => {
      this.signOutLocal();

      if (this.msalService.instance.getActiveAccount()) {
        await this.msalService.logoutPopup();
      }

      return true;
    })());
  }

  private signOutLocal() {
    this.webSocket.signOut();
  }

  finishCall(conversationKey: string, sessionId: string): Observable<any> {
    return this.webSocket.ejectFromMeeting(conversationKey, sessionId);
  }

  sendDtmf(dtmf: string, conversationKey: string): Observable<any> {
    return this.webSocket.sendDTMF(conversationKey, dtmf);
  }

  setReadyStatus(): Observable<any> {
    return this.webSocket.changePresence('Online', '');
  }
  setBusyStatus(): Observable<any> {
    return this.webSocket.changePresence(BUSY_PRESENSE, BUSY_NOTE);
  }

  pauseCall(conversationKey: string, sessionId: string): Observable<boolean> {
    return this.webSocket.pauseCall(conversationKey, sessionId);
  }
  resumeCall(conversationKey: string, sessionId: string): Observable<boolean> {
    return this.webSocket.resumeCall(conversationKey, sessionId);
  }

  public warmTransferStart(conversationKey: string, sessionId: string, targetUri: string): Observable<IOutgoingConversation> {
    return this.webSocket.warmTransferStart(conversationKey, sessionId, targetUri).pipe(
      map((res) => {
        let conversation = new CC4AllWarmTransferConversation(conversationKey, sessionId, targetUri, this);

        this._outgoingCall.next(conversation);
        return conversation;
      }),
    );
  }

  public warmTransferCancel(conversationKey: string, sessionId: string, targetUri: string): Observable<any> {
    return this.webSocket.warmTransferCancel(conversationKey, sessionId, targetUri);
  }

  public createOutboundCall(uri: string): Observable<IOutgoingConversation> {
    const callId = ((Math.random() * 1000) % 10000) + '';

    return this.webSocket.getEndPointListForAgent().pipe(
      mergeMap((res) => {
        console.log(res);
        let endpoint = res.find((x) => x.IsDefault) || res.find((x) => true);
        if (!endpoint) {
          throw Error('No active endpoints found.');
        }

        return this.webSocket.createOutboundCall(callId, endpoint.EndpointID, endpoint.EndpointLineUri, uri);
      }),
      mergeMap((res) => this.webSocket.callSessions$),
      filter((callSession) => callSession.ConversationKey === callId),
      map((callSession) => {
        console.log(callSession);
        let conversation = new CC4AllOutboundConversation(
          callSession.ConversationKey,
          callSession.SessionId,
          this.webSocket.sip,
          uri,
          this,
        );

        this._outgoingCall.next(conversation);
        return conversation;
      }),
      timeout(5000),
    );
  }

  createConversation(caller: string, callId: string, sessionId: string, agentSip: string): Observable<IConversation> {
    return this.webSocket.getAgentCallSession().pipe(
      map((res) => {
        if (!res || !res.ConversationKey) {
          throw Error('No active call session.');
        }

        let conversation = new CC4AllConversation(caller, agentSip, this);
        conversation.callId = res.ConversationKey;
        conversation.sessionId = sessionId;
        conversation.conversationKey = res.ConversationKey;

        this.setBusyStatus().subscribe((x) => {});
        return conversation;
      }),
    );
  }
}
